///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthAlertService } from '../../../services/alert.service';
import { GtthPreloaderModalEvent } from '../../../shared/preloader-modal/preloader-modal-event';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { parseFormDataToObj } from '../../../shared/utils';
import { GtthUpdateMainConfigCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/update-main-config-command';
import { GtthProjectStateService } from '../../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthHistoryService } from '../../../project-view-panel/logical-topology/engine/history-service';
import { GtthEntityNameValidation } from '../../../shared/directives/validation-functions/entity-name';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-layers-settings-dialog',
    templateUrl: './layers-settings-dialog.component.html',
    styleUrls: ['layers-settings-dialog.scss'],
    standalone: false
})
export class GtthLayersSettingsDialogComponent implements OnInit {
    public editForm: FormGroup;
    public layersControlsArr: FormArray;

    public layers: GtthLayerDtoModel[] = [];
    public layerNames: string[] = [];
    public activeLayerIndex = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public isSaving: boolean;

    constructor(
        private readonly alertService: GtthAlertService,
        private readonly eventManager: GtthEventManager,
        private readonly formBuilder: FormBuilder,
        private readonly historyService: GtthHistoryService,
        private readonly projectStateService: GtthProjectStateService,
        public dialogRef: MatDialogRef<GtthLayersSettingsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { id: number }
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.save();
        }
    }

    ngOnInit() {
        this.isSaving = false;

        if (this.historyService.logicalTopology) {
            this.layers = this.historyService.logicalTopology.layers;
            if (this.historyService.logicalTopology?.activeLayerIdx && this.historyService.logicalTopology?.activeLayerIdx !== -1) {
                this.activeLayerIndex = this.historyService.logicalTopology.activeLayerIdx;
            }
            this.initForm();
        }
    }

    initForm() {
        this.layersControlsArr = this.formBuilder.array([]);

        this.layers.forEach((layer: GtthLayerDtoModel) => {
            this.layerNames.push(layer.getFullName());
            const layerGroup = this.formBuilder.group({
                layerName: this.formBuilder.control(
                    layer.layerName,
                    Validators.compose([
                        Validators.required,
                        Validators.maxLength(250),
                        GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
                    ])
                )
            });

            this.layersControlsArr.push(layerGroup);
        });

        this.editForm = this.formBuilder.group({
            layers: this.layersControlsArr
        });
    }

    changeLayerTab(tab) {
        this.activeLayerIndex = this.getTabIndex(tab);
    }

    getTabIndex(tabName: string): number {
        return this.layerNames.indexOf(tabName);
    }

    save() {
        if (this.isSaving) {
            return;
        }

        for (let i = 0; i < this.layers.length; i++) {
            if (!this.validateLayersName(i)) {
                return;
            }
        }

        parseFormDataToObj(this.layers, this.editForm.get('layers'));

        this.isSaving = true;
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Saving')
        });

        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === this.data.id) {
            this.layers.forEach((layer: GtthLayerDtoModel, index: number) => {
                this.historyService.logicalTopology.layers[index] = new GtthLayerDtoModel(index, layer.layerName);
            });
            if (this.historyService.logicalTopology.layersModeEnabled) {
                this.historyService.projectInfoViewService.showLayers(
                    this.historyService.logicalTopology.layers,
                    this.historyService.logicalTopology.activeLayerIdx
                );
            }
        }

        this.updateProjectConfig();
    }

    validateLayersName(layerIndex: number): boolean {
        let layerNameIsValid = true;
        const layerNameControl = (this.editForm.get('layers') as FormGroup).controls[layerIndex];
        if (layerNameControl.value.layerName && this.layersControlsArr) {
            layerNameControl.get('layerName').setValue(layerNameControl.value.layerName.trim());

            const duplicateNameIndex = this.layersControlsArr.value.findIndex((layerControl, index) => {
                return layerIndex !== index && layerControl.layerName === layerNameControl.value.layerName;
            });
            if (duplicateNameIndex !== -1) {
                layerNameIsValid = false;
                this.alertService.showAlert({
                    msg: 'error.validation.field.layerNameExists',
                    data: { layerName: layerNameControl.value.layerName }
                });
            }
        } else if (!layerNameControl.value?.layerName) {
            layerNameIsValid = false;
            this.alertService.showAlert('error.validation.form.required');
        }
        return layerNameIsValid;
    }

    private updateProjectConfig() {
        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === this.data.id) {
            const updateMainConfigCommand = this.historyService.command(GtthUpdateMainConfigCommand);
            this.historyService.doCommandBypassHistory(updateMainConfigCommand).then(
                () => {
                    this.onSaveSuccess();
                },
                (err) => {
                    this.onSaveError(err);
                }
            );
        }
    }

    private onSaveSuccess() {
        this.eventManager.broadcast({ name: 'projectModified', content: 'OK' });
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.hide()
        });

        this.isSaving = false;
        this.dialogRef.close();
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save - ${exception}`);
        }
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.hide()
        });
        this.isSaving = false;
        this.onError(error);
    }

    private onError(error) {
        this.alertService.error(error.message, null, null);
    }
}

@Component({
    selector: 'winde-layers-popup',
    template: '',
    standalone: false
})
export class GtthLayersSettingsPopupComponent implements OnInit {
    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly historyService: GtthHistoryService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (!this.historyService.isEditModeEnabled || !isDefined(id)) {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        } else {
            const dialogRef = this.dialog.open(GtthLayersSettingsDialogComponent, {
                width: '920px',
                position: {
                    top: '30px'
                },
                data: {
                    id
                },
                closeOnNavigation: true
            });

            dialogRef.afterClosed().subscribe(() => {
                this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
            });
        }
    }
}
